import { VocabularyTerms } from '@ftr/contracts/type/core'
import { ONE_SECOND_MS } from '@ftr/contracts/type/shared'
import { LocalTimeRange } from '@ftr/contracts/type/shared/LocalTimeRange'
import { assertUnreachable, DateFormat, jodaFormatPatternWithLocale, titleCase } from '@ftr/foundation'
import { Duration, LocalTime } from '@js-joda/core'

const SUGGEST_TIME_FORMAT = DateFormat.TimeWithSeconds
export const HEARING_SECTION_CREATION_OUT_OF_BOUNDS_MESSAGE = (terms: VocabularyTerms): string =>
  `Cannot add a ${titleCase(terms.hearing.singular)} annotation at the specified time`
export const HEARING_SECTION_FUTURE_END_TIME_MESSAGE = (terms: VocabularyTerms): string =>
  `A ${titleCase(terms.hearing.singular)} annotation's end time cannot be in the future`
export const HEARING_SECTION_START_TIME_OUT_BOUNDS_MESSAGE = (
  suggestedTime: LocalTime | undefined = undefined,
): string => outOfBoundsMessage('start', suggestedTime)
export const HEARING_SECTION_END_TIME_OUT_BOUNDS_MESSAGE = (suggestedTime: LocalTime | undefined = undefined): string =>
  outOfBoundsMessage('end', suggestedTime)

function outOfBoundsMessage(type: 'start' | 'end', suggestedTime: LocalTime | undefined): string {
  return suggestedTime
    ? `${capitalizeFirstLetter(type)} time is not within a session. This session ${type}s at ${suggestedTime.format(jodaFormatPatternWithLocale(SUGGEST_TIME_FORMAT))}`
    : `${capitalizeFirstLetter(type)} time is not within a session`
}

function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}
export const HEARING_SECTION_START_AND_END_TIME_OUT_BOUNDS_MESSAGE = 'Start and end times are not within a session'

export const HEARING_SECTION_START_AND_END_TIME_IN_DIFFERENT_SESSIONS = 'Start and end times are in different sessions'
export const HEARING_SECTION_START_MUST_BE_BEFORE_END_MESSAGE = 'Start time must be before end time'
export type HearingAnnotationTimeValidationReason =
  | 'start-must-be-before-end'
  | 'start-is-out-of-bounds'
  | 'end-is-out-of-bounds'
  | 'both-out-of-bounds'
  | 'different-sessions'
  | 'future-end-time'
  | 'start-is-not-a-time'
  | 'end-is-not-a-time'
  | 'both-are-not-times'
export interface HearingAnnotationTimeValidation {
  reason: HearingAnnotationTimeValidationReason
  matchingStartBoundary?: LocalTimeRange
  matchingEndBoundary?: LocalTimeRange
}

/**
 * behaviour
 *
 * if startTime: start inclusive/end exclusive
 * if endTime: start exclusive/end inclusive
 */
export function validateHearingSectionTimes(
  startTime: LocalTime,
  endTime: LocalTime,
  onRecordTimeFrames: LocalTimeRange[],
  liveLocaltime: LocalTime | undefined,
): HearingAnnotationTimeValidation | undefined {
  if (startTime.equals(endTime) || startTime.isAfter(endTime)) {
    return { reason: 'start-must-be-before-end' }
  }
  if (Duration.between(startTime, endTime).toMillis() < ONE_SECOND_MS) {
    // Annotations can only be accurate to the second and must have at least 1 second duration
    return { reason: 'start-must-be-before-end' }
  }

  const startBoundary = getAnnotationBoundaryForStartTime(startTime, onRecordTimeFrames)
  const endBoundary = getAnnotationBoundaryForEndTime(endTime, onRecordTimeFrames)
  if (!startBoundary && !endBoundary) {
    return { reason: 'both-out-of-bounds' }
  }

  if (!startBoundary) {
    return { reason: 'start-is-out-of-bounds', matchingEndBoundary: endBoundary ?? undefined }
  }
  if (!endBoundary) {
    return { reason: 'end-is-out-of-bounds', matchingStartBoundary: startBoundary ?? undefined }
  }

  const sameSession = startBoundary?.encloses(endBoundary)
  if (!sameSession) {
    return { reason: 'different-sessions', matchingStartBoundary: startBoundary, matchingEndBoundary: endBoundary }
  }

  if (liveLocaltime?.isBefore(endTime)) {
    const isOngoing = endTime.equals(endBoundary.end)
    if (!isOngoing) {
      return { reason: 'future-end-time' }
    }
  }

  return undefined
}

export type InclusiveExclusiveMode = 'start-inclusive-end-exclusive' | 'start-exclusive-end-inclusive'

export function getTimeRangeForLocalTime(
  localTime: LocalTime,
  timeRanges: LocalTimeRange[],
  mode: InclusiveExclusiveMode,
): LocalTimeRange | undefined {
  if (mode === 'start-inclusive-end-exclusive') {
    return timeRanges.find(
      tr => (tr.start.equals(localTime) || tr.start.isBefore(localTime)) && tr.end.isAfter(localTime),
    )
  }

  return timeRanges.find(tr => tr.start.isBefore(localTime) && (tr.end.equals(localTime) || tr.end.isAfter(localTime)))
}

export function getAnnotationBoundaryForStartTime(
  startTime: LocalTime,
  timeRanges: LocalTimeRange[],
): LocalTimeRange | undefined {
  return getTimeRangeForLocalTime(startTime, timeRanges, 'start-inclusive-end-exclusive')
}

export function getAnnotationBoundaryForEndTime(
  endTime: LocalTime,
  timeRanges: LocalTimeRange[],
): LocalTimeRange | undefined {
  return getTimeRangeForLocalTime(endTime, timeRanges, 'start-exclusive-end-inclusive')
}

export function mapValidationReasonMessage(
  errorValidation: HearingAnnotationTimeValidation,
  terms: VocabularyTerms,
): string {
  switch (errorValidation.reason) {
    case 'start-is-out-of-bounds':
      return HEARING_SECTION_START_TIME_OUT_BOUNDS_MESSAGE(errorValidation.matchingEndBoundary?.start)
    case 'end-is-out-of-bounds':
      return HEARING_SECTION_END_TIME_OUT_BOUNDS_MESSAGE(errorValidation.matchingStartBoundary?.end)
    case 'both-out-of-bounds':
      return HEARING_SECTION_START_AND_END_TIME_OUT_BOUNDS_MESSAGE
    case 'different-sessions':
      return HEARING_SECTION_START_AND_END_TIME_IN_DIFFERENT_SESSIONS
    case 'start-must-be-before-end':
      return HEARING_SECTION_START_MUST_BE_BEFORE_END_MESSAGE
    case 'future-end-time':
      return HEARING_SECTION_FUTURE_END_TIME_MESSAGE(terms)
    case 'start-is-not-a-time':
      return 'Please enter a valid start time'
    case 'end-is-not-a-time':
      return 'Please enter a valid end time'
    case 'both-are-not-times':
      return 'Please enter valid times'
    default:
      return assertUnreachable(errorValidation.reason)
  }
}
